import React from 'react';
import {
    FOCUS_SEARCH_SHORTCUT,
    SALE_ESCAPE_SHORTCUT,
    useShortcut,
} from '@/components/shortcuts';
import { useSeller } from '../../useSeller';
import { useShift } from '../../shift/useShift';
import { useHomeFocus } from '../../HomeFocusProvider';
import {
    useSearchEnterToAdd,
    type SearchEnterNoticeKey,
} from './useSearchEnterToAdd';

/**
 * Every decision the product panel makes (bos-seller#47's focus model), so
 * `ProductPanel.tsx` only renders. Search is the sale screen's home position
 * with a shift open, and this hook owns it:
 *
 * - it registers how to focus search with `useHomeFocus`, so a sibling panel
 *   (the cart, when `Delete` empties it) can send focus home too;
 * - search takes focus on arrival with an open shift, and `F2` returns focus
 *   to it (its text selected) from anywhere on the screen;
 * - `Esc` is layered: an open overlay closes first (its own scope suspends
 *   `seller`, so this binding never runs under it); otherwise non-empty
 *   search text clears; otherwise focus goes home;
 * - in search, `↓` moves focus into the product grid and `Enter` follows
 *   `useSearchEnterToAdd`'s rules, whose notice key comes back to the caller
 *   to translate and toast;
 * - a letter or digit typed in the grid is appended to search, and focus
 *   moves there.
 *
 * "Home" routes (`goHome`: `Esc`, the category filter or the variant picker
 * closing by any route, an add by mouse or touch) go through `useHomeFocus`
 * rather than straight to search, so whichever control is home right now
 * receives focus: the Open-shift button while no shift is open (it claims
 * home, SEL-49), search otherwise. `F2` and the typed-letter redirect always
 * mean search itself.
 */
export const useProductPanel = () => {
    const seller = useSeller();
    const { search, setSearch, filteredProducts, variantPicker } = seller;
    const { isOpen: isShiftOpen } = useShift();
    const { registerFocusHome, focusHome: goHome } = useHomeFocus();

    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const productGridRef = React.useRef<HTMLDivElement>(null);

    const focusSearch = React.useCallback(() => {
        searchInputRef.current?.focus();
    }, []);

    React.useEffect(() => {
        registerFocusHome(focusSearch);
    }, [registerFocusHome, focusSearch]);

    React.useEffect(() => {
        if (isShiftOpen) {
            focusSearch();
        }
    }, [isShiftOpen, focusSearch]);

    useShortcut(FOCUS_SEARCH_SHORTCUT.id, () => {
        focusSearch();
        searchInputRef.current?.select();
    });

    useShortcut(SALE_ESCAPE_SHORTCUT.id, () => {
        if (search !== '') {
            setSearch('');
            return;
        }
        goHome();
    });

    // Whichever way the variant picker closes (choosing, Esc, the backdrop),
    // focus goes home: the same wasOpen-ref shape as `useVoidConfirm`.
    const isPickerOpen = variantPicker !== null;
    const wasPickerOpen = React.useRef(isPickerOpen);
    React.useEffect(() => {
        if (wasPickerOpen.current && !isPickerOpen) {
            goHome();
        }
        wasPickerOpen.current = isPickerOpen;
    }, [isPickerOpen, goHome]);

    const { onSearchKeyDown: onSearchEnter } = useSearchEnterToAdd({
        search,
        setSearch,
        filteredProducts,
        addByBarcode: seller.addByBarcode,
        addProduct: seller.addProduct,
        productGridRef,
    });

    const onSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ): SearchEnterNoticeKey | null => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            productGridRef.current?.focus();
            return null;
        }
        return onSearchEnter(event);
    };

    const onPrintableKey = (key: string) => {
        setSearch(search + key);
        focusSearch();
    };

    return {
        search,
        setSearch,
        filteredProducts,
        productsLoading: seller.productsLoading,
        productsError: seller.productsError,
        productsTruncated: seller.productsTruncated,
        inCartQty: seller.inCartQty,
        selectProduct: seller.selectProduct,
        variantPicker,
        chooseVariant: seller.chooseVariant,
        closeVariantPicker: seller.closeVariantPicker,
        searchInputRef,
        productGridRef,
        onSearchKeyDown,
        onPrintableKey,
        goHome,
    };
};
